/** Bulk "Cache control" — tiered cache, append a cache rule, and purge paths. */
import { useState } from 'react';
import {
  Switch,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { BulkSection } from './BulkSection';
import { useBulkApply } from './useBulkApply';
import styles from './WafBulkConfigure.module.css';

const SCOPES = [
  { value: 'extension', label: 'File extension' },
  { value: 'directory', label: 'Directory' },
  { value: 'pattern', label: 'Path pattern' },
];

const splitPaths = (s: string) =>
  Array.from(new Set(s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean)));

export function CacheControlTab({ serviceId, domains }: { serviceId: string; domains: string[] }) {
  const [tiered, setTiered] = useState(false);
  const tc = useBulkApply(serviceId, 'tiered-cache');

  const [scope, setScope] = useState('extension');
  const [path, setPath] = useState('');
  const [ttl, setTtl] = useState('3600');
  const [honor, setHonor] = useState(false);
  const rule = useBulkApply(serviceId, 'cache-rules');

  // No-cache (bypass) rule — a cache rule with ttl=0.
  const [noScope, setNoScope] = useState('extension');
  const [noPath, setNoPath] = useState('');
  const noCache = useBulkApply(serviceId, 'cache-rules');

  const [paths, setPaths] = useState('');
  const purge = useBulkApply(serviceId, 'purge');

  const ttlNum = Number(ttl);

  return (
    <>
      <BulkSection
        title="Tiered cache"
        hint="Multi-layer caching for better origin offload."
        onApply={() => tc.mutate({ domains, enable: tiered })}
        pending={tc.isPending}
      >
        <label className={styles.row} style={{ cursor: 'pointer' }}>
          <Switch checked={tiered} onCheckedChange={setTiered} aria-label="Tiered cache" />
          <span>{tiered ? 'Enabled' : 'Disabled'}</span>
        </label>
      </BulkSection>

      <BulkSection
        title="Add cache rule"
        hint="Append one cache rule to every selected domain."
        onApply={() => rule.mutate({ domains, scope, path: path.trim(), ttl: ttlNum, honor_origin: honor })}
        disabled={!path.trim() || !Number.isFinite(ttlNum) || ttlNum < 0}
        pending={rule.isPending}
      >
        <div className={styles.row}>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger aria-label="Scope" style={{ minWidth: 160 }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder=".jpg" value={path} onChange={(e) => setPath(e.target.value)} style={{ flex: '1 1 160px' }} />
          <Input
            type="number"
            min={0}
            value={ttl}
            onChange={(e) => setTtl(e.target.value)}
            aria-label="TTL seconds"
            style={{ width: 120 }}
          />
        </div>
        <label className={styles.row} style={{ cursor: 'pointer' }}>
          <Switch checked={honor} onCheckedChange={setHonor} aria-label="Honor origin" />
          <span>Honor origin Cache-Control</span>
        </label>
      </BulkSection>

      <BulkSection
        title="No cache rule"
        hint="Bypass the cache (ttl 0) for these paths on every selected domain."
        onApply={() => noCache.mutate({ domains, scope: noScope, path: noPath.trim(), ttl: 0, honor_origin: false })}
        disabled={!noPath.trim()}
        pending={noCache.isPending}
      >
        <div className={styles.row}>
          <Select value={noScope} onValueChange={setNoScope}>
            <SelectTrigger aria-label="No-cache scope" style={{ minWidth: 160 }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder=".php" value={noPath} onChange={(e) => setNoPath(e.target.value)} style={{ flex: '1 1 160px' }} />
        </div>
      </BulkSection>

      <BulkSection
        title="Purge cache"
        hint="Submit a purge job for these paths across every domain."
        onApply={() => purge.mutate({ domains, paths: splitPaths(paths) })}
        disabled={splitPaths(paths).length === 0}
        pending={purge.isPending}
        applyLabel="Purge"
      >
        <Input placeholder="/assets/app.js, /images/*" value={paths} onChange={(e) => setPaths(e.target.value)} />
      </BulkSection>
    </>
  );
}

export default CacheControlTab;
